import { useState, useMemo } from 'react';
import type { Goal } from '../../../hooks/useGoals';
import type { Account } from '../../../hooks/useAccounts';
import type { Card } from '../../../hooks/useCards';
import { CurrencyInput } from '../../../components/base/CurrencyInput';

export interface ContributionParams {
  amount: number;
  note: string;
  sourceType: 'account' | 'debit_card' | 'manual';
  sourceAccountId: string | null;
  sourceCardId: string | null;
  linkedAccountId: string | null;
  contributionDate: string;
  observation: string;
}

interface ContributionModalProps {
  open: boolean;
  goal: Goal | null;
  accounts: Account[];
  cards: Card[];
  onClose: () => void;
  onAdd: (params: ContributionParams) => Promise<{ error: string | null }>;
}

const fmt = (v: number) => v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
const todayStr = () => new Date().toISOString().split('T')[0];

const SOURCE_OPTIONS = [
  { value: 'account' as const, icon: 'ri-bank-line', label: 'Conta bancária' },
  { value: 'debit_card' as const, icon: 'ri-bank-card-line', label: 'Cartão de débito' },
  { value: 'manual' as const, icon: 'ri-pencil-line', label: 'Sem vínculo' },
];

export const ContributionModal = ({ open, goal, accounts, cards, onClose, onAdd }: ContributionModalProps) => {
  const [amount, setAmount] = useState<number>(0);
  const [note, setNote] = useState('');
  const [sourceType, setSourceType] = useState<'account' | 'debit_card' | 'manual'>('account');
  const [sourceAccountId, setSourceAccountId] = useState<string>('');
  const [sourceCardId, setSourceCardId] = useState<string>('');
  const [contributionDate, setContributionDate] = useState(todayStr());
  const [observation, setObservation] = useState('');
  const [saving, setSaving] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');

  const activeAccounts = useMemo(() => accounts.filter(a => !a.is_archived && a.is_active), [accounts]);
  const debitCards = useMemo(
    () => cards.filter(c => !c.is_archived && (c.card_type === 'debit' || c.card_type === 'multiple') && c.linked_account_id),
    [cards]
  );
  const creditCards = useMemo(
    () => cards.filter(c => !c.is_archived && c.card_type === 'credit'),
    [cards]
  );

  const effectiveAccountId = useMemo(() => {
    if (sourceType === 'account') return sourceAccountId || null;
    if (sourceType === 'debit_card') {
      const card = debitCards.find(c => c.id === sourceCardId);
      return card?.linked_account_id ?? null;
    }
    return null;
  }, [sourceType, sourceAccountId, sourceCardId, debitCards]);

  const selectedAccount = useMemo(
    () => accounts.find(a => a.id === effectiveAccountId) ?? null,
    [accounts, effectiveAccountId]
  );

  const selectedCard = useMemo(
    () => debitCards.find(c => c.id === sourceCardId) ?? null,
    [debitCards, sourceCardId]
  );

  const progressAfter = useMemo(() => {
    if (!goal) return 0;
    return Math.min(100, ((goal.current_amount + amount) / goal.target_amount) * 100);
  }, [goal, amount]);

  const remainingAfter = useMemo(() => {
    if (!goal) return 0;
    return Math.max(0, goal.target_amount - goal.current_amount - amount);
  }, [goal, amount]);

  const balanceAfter = useMemo(() => {
    if (!selectedAccount || amount <= 0) return null;
    return selectedAccount.current_balance - amount;
  }, [selectedAccount, amount]);

  const isValid = amount > 0 && (
    sourceType === 'manual' ||
    (sourceType === 'account' && !!sourceAccountId) ||
    (sourceType === 'debit_card' && !!sourceCardId)
  );

  const handleClose = () => {
    setAmount(0);
    setNote('');
    setSourceType('account');
    setSourceAccountId('');
    setSourceCardId('');
    setContributionDate(todayStr());
    setObservation('');
    setErrorMsg('');
    onClose();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isValid) return;
    setSaving(true);
    setErrorMsg('');
    const result = await onAdd({
      amount,
      note,
      sourceType,
      sourceAccountId: sourceType === 'account' ? sourceAccountId || null : null,
      sourceCardId: sourceType === 'debit_card' ? sourceCardId || null : null,
      linkedAccountId: effectiveAccountId,
      contributionDate,
      observation,
    });
    setSaving(false);
    if (result?.error) {
      setErrorMsg('Erro ao registrar aporte. Tente novamente.');
    } else {
      handleClose();
    }
  };

  if (!open || !goal) return null;

  const remaining = Math.max(0, goal.target_amount - goal.current_amount);
  const currentProgress = goal.target_amount > 0
    ? Math.min(100, (goal.current_amount / goal.target_amount) * 100)
    : 0;

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={handleClose} />
      <div className="relative bg-white rounded-2xl max-w-md w-full mx-4 overflow-hidden max-h-[92vh] flex flex-col">

        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0"
              style={{ backgroundColor: goal.color + '20' }}>
              <i className={`${goal.icon || 'ri-trophy-line'} text-base`} style={{ color: goal.color }} />
            </div>
            <div>
              <h3 className="text-sm font-700 text-gray-900">Registrar aporte</h3>
              <p className="text-xs text-gray-400 truncate max-w-[200px]">{goal.name} · faltam {fmt(remaining)}</p>
            </div>
          </div>
          <button onClick={handleClose}
            className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 text-gray-400 cursor-pointer">
            <i className="ri-close-line" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto px-5 py-4 space-y-4">

          {/* Valor + Data side by side */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-600 text-gray-500 uppercase tracking-wide mb-1.5 block">Valor (R$) *</label>
              <CurrencyInput
                required
                value={amount}
                onChange={(v) => setAmount(v)}
                placeholder="0,00"
                className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-emerald-400 focus:ring-2 focus:ring-emerald-50"
              />
            </div>
            <div>
              <label className="text-xs font-600 text-gray-500 uppercase tracking-wide mb-1.5 block">Data do aporte</label>
              <input
                type="date" value={contributionDate}
                onChange={e => setContributionDate(e.target.value || todayStr())}
                className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-emerald-400"
              />
            </div>
          </div>

          {/* Descrição */}
          <div>
            <label className="text-xs font-600 text-gray-500 uppercase tracking-wide mb-1.5 block">
              Descrição <span className="normal-case text-gray-400 font-400">(opcional)</span>
            </label>
            <input
              value={note} onChange={e => setNote(e.target.value)} maxLength={120}
              placeholder="Ex: Salário de março, bônus, renda extra..."
              className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-emerald-400"
            />
          </div>

          {/* Origem */}
          <div>
            <label className="text-xs font-600 text-gray-500 uppercase tracking-wide mb-2 block">Origem do dinheiro *</label>
            <div className="grid grid-cols-3 gap-2">
              {SOURCE_OPTIONS.map(opt => {
                const isDisabled = opt.value === 'debit_card' && debitCards.length === 0;
                return (
                  <button
                    key={opt.value}
                    type="button"
                    disabled={isDisabled}
                    onClick={() => { setSourceType(opt.value); setSourceAccountId(''); setSourceCardId(''); }}
                    className={`flex flex-col items-center gap-1.5 px-2 py-3 rounded-xl border-2 transition-all cursor-pointer ${
                      sourceType === opt.value
                        ? 'border-emerald-400 bg-emerald-50'
                        : isDisabled
                          ? 'border-gray-100 bg-gray-50 opacity-40 cursor-not-allowed'
                          : 'border-gray-100 hover:border-gray-200 bg-white'
                    }`}
                  >
                    <div className={`w-7 h-7 flex items-center justify-center rounded-lg ${
                      sourceType === opt.value ? 'bg-emerald-100' : 'bg-gray-100'
                    }`}>
                      <i className={`${opt.icon} text-sm ${sourceType === opt.value ? 'text-emerald-600' : 'text-gray-400'}`} />
                    </div>
                    <span className={`text-xs font-600 leading-tight text-center ${
                      sourceType === opt.value ? 'text-emerald-700' : 'text-gray-500'
                    }`}>{opt.label}</span>
                  </button>
                );
              })}
            </div>
            {sourceType === 'manual' && (
              <p className="text-xs text-gray-400 mt-2 flex items-center gap-1">
                <i className="ri-information-line" />
                Apenas registra o valor na meta sem movimentar nenhuma conta.
              </p>
            )}
          </div>

          {/* Seleção de conta */}
          {sourceType === 'account' && (
            <div>
              <label className="text-xs font-600 text-gray-500 uppercase tracking-wide mb-1.5 block">Selecionar conta *</label>
              {activeAccounts.length === 0 ? (
                <div className="flex items-center gap-2 px-3 py-2.5 bg-amber-50 border border-amber-100 rounded-xl text-xs text-amber-700">
                  <i className="ri-error-warning-line" />Nenhuma conta ativa cadastrada.
                </div>
              ) : (
                <select
                  required value={sourceAccountId}
                  onChange={e => setSourceAccountId(e.target.value)}
                  className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-emerald-400 bg-white"
                >
                  <option value="">Escolher conta...</option>
                  {activeAccounts.map(acc => (
                    <option key={acc.id} value={acc.id}>
                      {acc.name}{acc.bank_name ? ` — ${acc.bank_name}` : ''} · {fmt(acc.current_balance)}
                    </option>
                  ))}
                </select>
              )}
            </div>
          )}

          {/* Seleção de cartão de débito */}
          {sourceType === 'debit_card' && (
            <div>
              <label className="text-xs font-600 text-gray-500 uppercase tracking-wide mb-1.5 block">Selecionar cartão *</label>
              {debitCards.length === 0 ? (
                <div className="flex items-center gap-2 px-3 py-2.5 bg-amber-50 border border-amber-100 rounded-xl text-xs text-amber-700">
                  <i className="ri-error-warning-line" />Nenhum cartão de débito com conta vinculada encontrado.
                </div>
              ) : (
                <>
                  <select
                    required value={sourceCardId}
                    onChange={e => setSourceCardId(e.target.value)}
                    className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-emerald-400 bg-white"
                  >
                    <option value="">Escolher cartão...</option>
                    {debitCards.map(card => {
                      const linkedAcc = accounts.find(a => a.id === card.linked_account_id);
                      return (
                        <option key={card.id} value={card.id}>
                          {card.name} ····{card.last_four_digits}{linkedAcc ? ` · ${linkedAcc.name}` : ''}
                        </option>
                      );
                    })}
                  </select>
                  {selectedCard && selectedAccount && (
                    <p className="text-xs text-gray-400 mt-1.5 flex items-center gap-1">
                      <i className="ri-link-m text-xs" />
                      Débito sairá de <span className="font-600 text-gray-600">{selectedAccount.name}</span>
                    </p>
                  )}
                </>
              )}
              {creditCards.length > 0 && (
                <div className="mt-2 flex items-center gap-2 px-3 py-2 bg-gray-50 border border-gray-100 rounded-xl text-xs text-gray-500">
                  <i className="ri-lock-line text-gray-400" />
                  Cartões de crédito não são permitidos para aportes — aportar via crédito geraria uma dívida, não uma economia.
                </div>
              )}
            </div>
          )}

          {/* Observação opcional */}
          <div>
            <label className="text-xs font-600 text-gray-500 uppercase tracking-wide mb-1.5 block">
              Observação <span className="normal-case text-gray-400 font-400">(opcional)</span>
            </label>
            <textarea
              rows={2} maxLength={300} value={observation}
              onChange={e => setObservation(e.target.value)}
              placeholder="Detalhes adicionais sobre este aporte..."
              className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-emerald-400 resize-none"
            />
          </div>

          {/* Impacto do aporte */}
          {amount > 0 && (
            <div className="rounded-xl border border-emerald-100 bg-emerald-50/60 p-4">
              <p className="text-xs font-700 text-emerald-700 uppercase tracking-wide mb-3 flex items-center gap-1.5">
                <i className="ri-bar-chart-box-line text-emerald-500" />
                Impacto após este aporte
              </p>
              <div className="space-y-2.5">
                {/* Progress bar comparison */}
                <div>
                  <div className="flex items-center justify-between text-xs mb-1">
                    <span className="text-gray-500">Progresso atual</span>
                    <span className="font-700 text-gray-600">{currentProgress.toFixed(1)}%</span>
                  </div>
                  <div className="h-2 bg-white/80 rounded-full overflow-hidden border border-emerald-100">
                    <div className="h-full rounded-full" style={{ width: `${currentProgress}%`, backgroundColor: goal.color + '80' }} />
                  </div>
                </div>
                <div>
                  <div className="flex items-center justify-between text-xs mb-1">
                    <span className="text-gray-500">Após o aporte</span>
                    <span className="font-700 text-emerald-700">{progressAfter.toFixed(1)}%</span>
                  </div>
                  <div className="h-2 bg-white/80 rounded-full overflow-hidden border border-emerald-100">
                    <div className="h-full rounded-full transition-all duration-500"
                      style={{ width: `${progressAfter}%`, backgroundColor: goal.color }} />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-2 pt-1">
                  <div className="bg-white/70 rounded-lg px-3 py-2">
                    <p className="text-xs text-gray-400">Faltará após</p>
                    <p className="text-sm font-700 text-gray-700">{remainingAfter > 0 ? fmt(remainingAfter) : '🎉 Meta atingida!'}</p>
                  </div>
                  {balanceAfter !== null && (
                    <div className={`rounded-lg px-3 py-2 ${balanceAfter < 0 ? 'bg-red-50 border border-red-100' : 'bg-white/70'}`}>
                      <p className="text-xs text-gray-400">Saldo da conta após</p>
                      <p className={`text-sm font-700 ${balanceAfter < 0 ? 'text-red-600' : 'text-gray-700'}`}>
                        {fmt(balanceAfter)}
                        {balanceAfter < 0 && <span className="text-xs font-400 ml-1">(negativo!)</span>}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {errorMsg && (
            <div className="flex items-center gap-2 px-3 py-2.5 bg-red-50 border border-red-100 rounded-xl text-sm text-red-600">
              <i className="ri-error-warning-line" />{errorMsg}
            </div>
          )}

          <div className="flex gap-3 pt-1 pb-1">
            <button type="button" onClick={handleClose}
              className="flex-1 py-2.5 border border-gray-200 text-gray-600 font-600 rounded-xl text-sm cursor-pointer whitespace-nowrap hover:bg-gray-50">
              Cancelar
            </button>
            <button type="submit" disabled={saving || !isValid}
              className="flex-1 py-2.5 bg-emerald-500 hover:bg-emerald-600 disabled:opacity-50 text-white font-600 rounded-xl text-sm cursor-pointer whitespace-nowrap">
              {saving ? 'Registrando...' : 'Confirmar aporte'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
